import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {IconRegistryEntry} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

/**
 * Stable scaffold icon UUIDs. `name` is the Atlassian Icon Explorer id
 * (`@atlaskit/icon/core/{name}`, or `@atlaskit/icon-lab/core/{name}` for lab icons).
 */
export const SCAFFOLD_ICON_IDS = {
    aiSparkle: "25908e2e-eba6-4cfb-81b1-802e4c882176" as Uuid,
    apps: "8c9c9e40-a76e-4d6a-8c54-9885edaf22ec" as Uuid,
    bookWithBookmark: "3eca641b-2578-423e-b277-39df4ad97986" as Uuid,
    briefcase: "c74c940f-c787-4277-ba60-d624a33497de" as Uuid,
    chartBar: "8e9414d6-2a60-43c2-941c-7e74499852bb" as Uuid,
    chartBarLine: "e9de4c76-fedf-4214-a463-82b8d588bf31" as Uuid,
    chartTrendUp: "e2157de7-2f23-442d-a7f5-b4342ad84399" as Uuid,
    checkCircle: "5cf730c4-b205-4ced-86ad-a33ddfa58b26" as Uuid,
    component: "b6315bf7-db74-412b-b05c-7bd60ddc7995" as Uuid,
    dashboard: "5ca012ad-424a-41f1-93d1-0453e44f6bfe" as Uuid,
    dataFlow: "18fdeb34-179c-45d1-b693-b22dfbe7be0b" as Uuid,
    database: "5040df0d-ea2f-4232-9245-b26bd3b05d66" as Uuid,
    flag: "9c3d4e5f-6071-4c8d-8e9f-0a1b2c3d4e5f" as Uuid,
    folderClosed: "5d745660-0e5c-46e5-a183-3d17d9498974" as Uuid,
    globe: "8b2c3d4e-5f60-4b7c-8d8e-9f0a1b2c3d4e" as Uuid,
    grid: "0d4e5f60-7182-4d9e-8f01-1b2c3d4e5f60" as Uuid,
    image: "9fa9949c-c053-4f15-8021-5d0e7e73190b" as Uuid,
    lightbulb: "877826da-5e70-4fcc-b2d0-b0360f3ebd60" as Uuid,
    library: "b61a8617-fa99-4a75-af34-f844ac3cdd38" as Uuid,
    marketplace: "79cdc60a-de6f-4e68-b0e5-c9585a4e82f0" as Uuid,
    listChecklist: "418293a4-b5c6-4122-b345-5f60718293a4" as Uuid,
    megaphone: "2c8676fa-5154-488f-bda8-843da57308e1" as Uuid,
    officeBuilding: "d4e5f607-1829-4a3b-8c5d-6e7f8091a2b3" as Uuid,
    page: "6c8eef7b-b01d-4e04-95fa-1a6e71afc2e3" as Uuid,
    peopleGroup: "b435f435-efcd-405c-a221-afc5935f003c" as Uuid,
    person: "a924c49b-4a7e-4f59-81a4-c72441e578fd" as Uuid,
    pages: "1c887bd7-684e-4946-8439-3855590cc0fd" as Uuid,
    personAdd: "b436aa90-23e0-4721-8311-5eff47bfcd6b" as Uuid,
    personOffboard: "b6534609-9636-4cec-8d19-611f8666bce2" as Uuid,
    phone: "e80ae928-f7b0-4fb1-9094-a5f5e352d485" as Uuid,
    powerPlug: "30718293-a4b5-4011-a234-4e5f60718293" as Uuid,
    pulse: "1e5f6071-8293-4e0f-8012-2c3d4e5f6071" as Uuid,
    shapes: "a7e4b786-a0a2-42a6-be11-7c51a8ae861f" as Uuid,
    shield: "5293a4b5-c6d7-4233-c456-60718293a4b5" as Uuid,
    smartLinkList: "4f8a2c1e-6b3d-4789-a012-7e9c5d4b3a20" as Uuid,
    starStarred: "c5d9d565-9a38-4925-aaec-39d6a5e0cde8" as Uuid,
    table: "2f607182-93a4-4f10-9123-3d4e5f607182" as Uuid,
    tag: "b612b17d-7e26-4d5c-b912-f67dccf10b8b" as Uuid,
    tools: "60a94fd5-720f-4bb6-80a6-11d6bc3167d7" as Uuid,
    tree: "7a1b2c3d-4e5f-4a6b-8c7d-8e9f0a1b2c3d" as Uuid,
    vehicleCar: "650fc9d4-e46f-4f78-b1d4-8a961c9dbb01" as Uuid,
} as const;

export type ScaffoldIconExplorerName =
    | "ai-sparkle"
    | "apps"
    | "book-with-bookmark"
    | "briefcase"
    | "chart-bar"
    | "chart-bar-line"
    | "chart-trend-up"
    | "check-circle"
    | "component"
    | "dashboard"
    | "data-flow"
    | "database"
    | "flag"
    | "folder-closed"
    | "globe"
    | "grid"
    | "image"
    | "lightbulb"
    | "library"
    | "list-checklist"
    | "marketplace"
    | "megaphone"
    | "office-building"
    | "page"
    | "pages"
    | "people-group"
    | "person"
    | "person-add"
    | "person-offboard"
    | "phone"
    | "power-plug"
    | "pulse"
    | "shapes"
    | "shield"
    | "smart-link-list"
    | "star-starred"
    | "table"
    | "tag"
    | "tools"
    | "tree"
    | "vehicle-car";

type ScaffoldIconDefinition = IconRegistryEntry & {
    name: ScaffoldIconExplorerName;
};

export const SCAFFOLD_ICON_REGISTRY: Record<Uuid, ScaffoldIconDefinition> = {
    [SCAFFOLD_ICON_IDS.aiSparkle]: {
        name: "ai-sparkle",
        displayName: "ChatGPT",
    },
    [SCAFFOLD_ICON_IDS.apps]: {
        name: "apps",
        displayName: "Apps",
    },
    [SCAFFOLD_ICON_IDS.bookWithBookmark]: {
        name: "book-with-bookmark",
        displayName: "Book with bookmark",
    },
    [SCAFFOLD_ICON_IDS.briefcase]: {
        name: "briefcase",
        displayName: "Briefcase",
    },
    [SCAFFOLD_ICON_IDS.chartBar]: {
        name: "chart-bar",
        displayName: "Chart bar",
    },
    [SCAFFOLD_ICON_IDS.chartBarLine]: {
        name: "chart-bar-line",
        displayName: "Chart bar line",
    },
    [SCAFFOLD_ICON_IDS.chartTrendUp]: {
        name: "chart-trend-up",
        displayName: "Chart trend up",
    },
    [SCAFFOLD_ICON_IDS.checkCircle]: {
        name: "check-circle",
        displayName: "Check circle",
    },
    [SCAFFOLD_ICON_IDS.component]: {
        name: "component",
        displayName: "Component",
    },
    [SCAFFOLD_ICON_IDS.dashboard]: {
        name: "dashboard",
        displayName: "Dashboard",
    },
    [SCAFFOLD_ICON_IDS.dataFlow]: {
        name: "data-flow",
        displayName: "Data flow",
    },
    [SCAFFOLD_ICON_IDS.database]: {
        name: "database",
        displayName: "Database",
    },
    [SCAFFOLD_ICON_IDS.flag]: {
        name: "flag",
        displayName: "Flag",
    },
    [SCAFFOLD_ICON_IDS.folderClosed]: {
        name: "folder-closed",
        displayName: "Folder closed",
    },
    [SCAFFOLD_ICON_IDS.globe]: {
        name: "globe",
        displayName: "Globe",
    },
    [SCAFFOLD_ICON_IDS.grid]: {
        name: "grid",
        displayName: "Grid",
    },
    [SCAFFOLD_ICON_IDS.image]: {
        name: "image",
        displayName: "Image",
    },
    [SCAFFOLD_ICON_IDS.lightbulb]: {
        name: "lightbulb",
        displayName: "Lightbulb",
    },
    [SCAFFOLD_ICON_IDS.library]: {
        name: "library",
        displayName: "Library",
    },
    [SCAFFOLD_ICON_IDS.marketplace]: {
        name: "marketplace",
        displayName: "Marketplace",
    },
    [SCAFFOLD_ICON_IDS.listChecklist]: {
        name: "list-checklist",
        displayName: "List checklist",
    },
    [SCAFFOLD_ICON_IDS.megaphone]: {
        name: "megaphone",
        displayName: "Megaphone",
    },
    [SCAFFOLD_ICON_IDS.officeBuilding]: {
        name: "office-building",
        displayName: "Office building",
    },
    [SCAFFOLD_ICON_IDS.page]: {
        name: "page",
        displayName: "Page",
    },
    [SCAFFOLD_ICON_IDS.peopleGroup]: {
        name: "people-group",
        displayName: "People group",
    },
    [SCAFFOLD_ICON_IDS.person]: {
        name: "person",
        displayName: "Person",
    },
    [SCAFFOLD_ICON_IDS.pages]: {
        name: "pages",
        displayName: "Pages",
    },
    [SCAFFOLD_ICON_IDS.personAdd]: {
        name: "person-add",
        displayName: "Person add",
    },
    [SCAFFOLD_ICON_IDS.personOffboard]: {
        name: "person-offboard",
        displayName: "Person offboard",
    },
    [SCAFFOLD_ICON_IDS.phone]: {
        name: "phone",
        displayName: "Phone",
    },
    [SCAFFOLD_ICON_IDS.powerPlug]: {
        name: "power-plug",
        displayName: "Power plug",
    },
    [SCAFFOLD_ICON_IDS.pulse]: {
        name: "pulse",
        displayName: "Pulse",
    },
    [SCAFFOLD_ICON_IDS.shapes]: {
        name: "shapes",
        displayName: "Shapes",
    },
    [SCAFFOLD_ICON_IDS.shield]: {
        name: "shield",
        displayName: "Shield",
    },
    [SCAFFOLD_ICON_IDS.smartLinkList]: {
        name: "smart-link-list",
        displayName: "Smart link list",
    },
    [SCAFFOLD_ICON_IDS.starStarred]: {
        name: "star-starred",
        displayName: "Star starred",
    },
    [SCAFFOLD_ICON_IDS.table]: {
        name: "table",
        displayName: "Table",
    },
    [SCAFFOLD_ICON_IDS.tag]: {
        name: "tag",
        displayName: "Tag",
    },
    [SCAFFOLD_ICON_IDS.tools]: {
        name: "tools",
        displayName: "Tools",
    },
    [SCAFFOLD_ICON_IDS.tree]: {
        name: "tree",
        displayName: "Tree",
    },
    [SCAFFOLD_ICON_IDS.vehicleCar]: {
        name: "vehicle-car",
        displayName: "Vehicle car",
    },
};

export function buildScaffoldIconRegistryForConfig(): Record<string, IconRegistryEntry> {
    const registry: Record<string, IconRegistryEntry> = {};
    for (const [iconId, entry] of Object.entries(SCAFFOLD_ICON_REGISTRY)) {
        registry[iconId] = {
            displayName: entry.displayName,
            name: entry.name,
        };
    }
    return registry;
}

export function resolveScaffoldIconExplorerName(iconId: string): ScaffoldIconExplorerName | null {
    const entry = SCAFFOLD_ICON_REGISTRY[iconId as Uuid];
    if (entry === undefined) {
        return null;
    }
    return entry.name;
}
